import { Button, Chip, ConfirmButton, ErrorBanner, SkeletonRows, toast } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import * as React from 'react'
import { describeError } from '../../lib/api/client'
import type { StaleCloseRowOut } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { staleClosesQuery, useCloseStaleNow, useConfirmStaleBacklog, usePauseStaleClose, useResumeStaleClose } from './api'

const STAGES: Record<StaleCloseRowOut['stage'], string> = {
  backlog_review: 'Needs backlog review',
  ready: 'Closing notice is due',
  notice_shadow: 'Notice drafted in shadow',
  closing_soon: 'Closing soon',
  close_due: 'Close is due',
  paused: 'Paused',
  reply_received: 'Customer replied',
}

function shortDate(value: string | null | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : formatWhen(date.toISOString(), { style: 'absolute' })
}

function StaleRow({
  row,
  manager,
  checked,
  onChecked,
}: {
  row: StaleCloseRowOut
  manager: boolean
  checked: boolean
  onChecked: () => void
}) {
  const pause = usePauseStaleClose()
  const resume = useResumeStaleClose()
  const close = useCloseStaleNow()
  const busy = pause.isPending || resume.isPending || close.isPending
  const fail = pause.error ?? resume.error ?? close.error
  const act = <T,>(
    mutation: { mutate: (value: T, options: { onSuccess: () => void; onError: (error: Error) => void }) => void },
    value: T,
    done: string,
  ) =>
    mutation.mutate(value, {
      onSuccess: () => toast.success(done),
      onError: (error) => toast.error(describeError(error)),
    })

  return (
    <li className="rounded-lg border border-border bg-card p-3" data-testid="stale-close-row">
      <div className="flex items-start gap-2.5">
        {manager && row.backlog ? (
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 accent-primary"
            aria-label={`Select ${row.case_number}`}
            checked={checked}
            onChange={onChecked}
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <Link to="/case/$id" params={{ id: row.case_number }} className="font-semibold text-primary">
              {row.case_number}
            </Link>
            <span className="truncate font-medium">{row.subject}</span>
            <Chip tone={row.stage === 'close_due' ? 'danger' : row.paused ? 'neutral' : 'warning'}>{STAGES[row.stage]}</Chip>
            <span className="ml-auto text-xs text-subtle">
              {row.business_days_waiting} business day{row.business_days_waiting === 1 ? '' : 's'} waiting
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Ticket #{row.ticket_number}
            {row.contact_name ? ` · ${row.contact_name}` : ''}
            {row.notice_at ? ` · Notice ${shortDate(row.notice_at)}` : ''}
            {row.close_due_at ? ` · Close ${shortDate(row.close_due_at)}` : ''}
          </p>
          {row.pause_reason ? <p className="mt-1 text-xs text-muted-foreground">{row.pause_reason}</p> : null}
          {manager && row.stage !== 'reply_received' ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {row.paused ? (
                <Button size="sm" variant="outline" disabled={busy} onClick={() => act(resume, row.case_id, `${row.case_number} resumed`)}>
                  Resume
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => act(pause, { caseId: row.case_id, reason: 'Paused from the Stale view' }, `${row.case_number} paused`)}
                >
                  Pause
                </Button>
              )}
              <ConfirmButton
                size="sm"
                variant="ghost"
                disabled={busy}
                disabledReason={busy ? 'Another stale-close action is in progress…' : undefined}
                confirmLabel="Close with resolution, no time, and no email?"
                onConfirm={() => act(close, row.case_id, `${row.case_number} closed`)}
              >
                Close now
              </ConfirmButton>
            </div>
          ) : null}
          {fail ? (
            <p className="mt-1 text-xs text-destructive">
              {describeError(fail)}{' '}
              {close.error ? (
                <Link to="/case/$id" params={{ id: row.case_number }} search={{ action: 'close' }} className="underline">
                  Open this case's Close dialog
                </Link>
              ) : null}
            </p>
          ) : null}
        </div>
      </div>
    </li>
  )
}

/** Manager-reviewed #342 stale backlog and the live notice/close queue. */
export function StaleClosePanel({ manager }: { manager: boolean }) {
  const query = useQuery(staleClosesQuery())
  const approve = useConfirmStaleBacklog()
  const [selected, setSelected] = React.useState<Set<string>>(new Set())
  if (query.isPending) return <SkeletonRows count={4} />
  if (query.isError) {
    return (
      <ErrorBanner title="Couldn't load stale customer waits" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
    )
  }
  const data = query.data
  const backlog = data.rows.filter((row) => row.backlog)
  const choose = (id: string) =>
    setSelected((old) => {
      const next = new Set(old)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const submitBacklog = (all: boolean) =>
    approve.mutate(
      { all, caseIds: [...selected] },
      {
        onSuccess: () => {
          setSelected(new Set())
          toast.success('Stale backlog reviewed')
        },
        onError: (error) => toast.error(describeError(error)),
      },
    )

  return (
    <div className="space-y-3" data-testid="stale-close-panel">
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip tone={data.mode === 'auto' ? 'success' : data.mode === 'shadow' ? 'primary' : 'neutral'}>Courtesy close: {data.mode}</Chip>
        <span className="text-sm text-muted-foreground">
          Notice after {data.stale_business_days} business days; close {data.close_after_business_days} business days later.
        </span>
      </div>
      {!data.backlog_reviewed && backlog.length ? (
        <section className="rounded-lg border border-warning/40 bg-warning-soft p-3" aria-label="One-time stale backlog review">
          <p className="font-medium">Review the existing stale backlog before auto can send</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Confirm every listed case, or select the ones that should receive a closing notice. Unselected cases are paused. This review
            happens once.
          </p>
          {data.mode !== 'auto' ? (
            <p className="mt-2 text-sm">
              Courtesy close is {data.mode}. A manager must promote it to Auto in{' '}
              <Link to="/admin/settings" hash="automation" className="font-medium text-primary">
                Automation settings
              </Link>{' '}
              before confirming this batch.
            </p>
          ) : manager ? (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <ConfirmButton
                size="sm"
                variant="outline"
                disabled={approve.isPending}
                disabledReason={approve.isPending ? 'Saving the backlog review…' : undefined}
                confirmLabel={`Send notices for all ${backlog.length}?`}
                onConfirm={() => submitBacklog(true)}
              >
                Review all {backlog.length}
              </ConfirmButton>
              <ConfirmButton
                size="sm"
                variant="outline"
                disabled={approve.isPending || selected.size === 0}
                disabledReason={approve.isPending ? 'Saving the backlog review…' : 'Select at least one case first'}
                confirmLabel={`Approve ${selected.size}; pause ${backlog.length - selected.size}?`}
                onConfirm={() => submitBacklog(false)}
              >
                Review selected ({selected.size})
              </ConfirmButton>
            </div>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">A manager must confirm this one-time backlog.</p>
          )}
          {approve.error ? <p className="mt-1 text-xs text-destructive">{describeError(approve.error)}</p> : null}
        </section>
      ) : null}
      {data.rows.length ? (
        <ul className="space-y-2">
          {data.rows.map((row) => (
            <StaleRow
              key={row.case_id}
              row={row}
              manager={manager}
              checked={selected.has(row.case_id)}
              onChecked={() => choose(row.case_id)}
            />
          ))}
        </ul>
      ) : (
        <p className="py-3 text-sm text-muted-foreground">No customer waits are stale or closing soon.</p>
      )}
    </div>
  )
}
